<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-height="90%"
    scrollable
    :disabled="disabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-card data-cy="confirm-customLabels-modal">
      <v-card-title class="d-flex justify-space-between">
        {{ selectLabelsDialogTitle }}
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="scrollable">
        <!-- Show loading spinner while data is being fetched -->
        <div v-if="loading" class="d-flex justify-center">
          <v-progress-circular indeterminate color="primary" :aria-label="$t('common.loading')" />
        </div>
        <div v-else>
          <div v-if="showCustomLabels" class="d-flex align-center mb-4">
            <v-text-field
              v-model="customLabelInput"
              @keyup.enter="addCustomLabel"
              class="mr-4"
              variant="solo-filled"
              hide-details="auto"
              :error-messages="errorMessage"
              :label="$t('routes.pod.menuEditLabels.customLabel')"
              flat
            />

            <div style="align-self: flex-start;">
              <v-btn
                color="secondaryButtons"
                @click="addCustomLabel"
                variant="flat"
                prepend-icon="mdi-plus-circle"
              >
                {{ $t('routes.pod.menuEditLabels.addLabel') }}
              </v-btn>
            </div>
          </div>

          <!-- Include Sub-folders checkbox -->
          <div v-if="selectedNodes.length > 0" class="my-2">
            <v-card variant="outlined" class="pa-4">
              <v-checkbox
                v-model="includeSubfolders"
                :label="$t('routes.pod.menuEditLabels.includeSubfolders')"
                color="primary"
                density="comfortable"
                hide-details
              />
              <div class="font-italic text-medium-emphasis ml-2">
                {{ $t('routes.pod.menuEditLabels.includeSubfoldersDescription') }}
              </div>
            </v-card>
          </div>

          <!-- Chips view custom labels -->
          <div v-if="showCustomLabels" class="chips-container">
            <v-chip
              v-for="(label, idx) in selectedLabels.filter((v) => v.isCustom)"
              :key="label.name.replaceAll(/[^a-z0-9]+/gi, '_').toLowerCase()"
              @click:close="removeLabel(idx)"
              closable
              class="ma-2"
              label
            >
              {{ label.name }}
            </v-chip>
          </div>

          <div class="labels-tree">
            <v-card border>
              <v-card-text>
                <h4>{{ $t('routes.pod.menuEditLabels.predefinedLabels') }}</h4>
                <v-treeview
                  v-model="selectedLabels"
                  :items="categories"
                  item-title="name"
                  item-value="id"
                  :label="$t('routes.pod.menuEditLabels.predefinedLabels')"
                  variant="flat"
                  select-strategy="classic"
                  density="compact"
                  selectable return-object open-all
                >
                  <template v-slot:toggle="{ props, isOpen }">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      density="comfortable"
                      :icon="isOpen ? '$treeviewCollapse' : '$treeviewExpand'"
                      :aria-label="isOpen ? $t('common.collapse') : $t('common.expand')"
                      size="32"
                    />
                  </template>

                  <template v-slot:item="{ props, item }">
                    <v-treeview-item v-bind="props" :value="item">
                      <template v-slot:prepend="{ select, isSelected }">
                        <v-checkbox-btn
                          :model-value="isSelected"
                          @update:model-value="(v) => select(v)"
                          tabindex="-1"
                          aria-hidden="true"
                        />
                      </template>
                    </v-treeview-item>
                  </template>

                  <template v-slot:header="{ props, item }">
                    <v-treeview-item v-bind="props" :value="item">
                      <template v-slot:toggle="{ props, isOpen }">
                        <v-btn
                          v-bind="props"
                          variant="text"
                          density="comfortable"
                          :icon="isOpen ? '$treeviewCollapse' : '$treeviewExpand'"
                          :aria-label="isOpen ? $t('common.collapse') : $t('common.expand')"
                          size="32"
                        />
                      </template>

                      <template v-slot:prepend="{ select, isSelected, isIndeterminate }">
                        <v-checkbox-btn
                          :model-value="isSelected"
                          @update:model-value="(v) => select(v)"
                          :aria-label="$t('common.toggle')"
                          :indeterminate="isIndeterminate"
                        />
                      </template>
                    </v-treeview-item>
                  </template>
                </v-treeview>
              </v-card-text>
            </v-card>
          </div>

          <div v-if="!hasLabels" class="font-italic">
            {{ $t('routes.pod.menuEditLabels.noLabels') }}
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-btn
          :disabled="!hasSelectedLabels || isProcessingLabels"
          class="ml-auto"
          color="primary"
          variant="flat"
          elevation=0
          @click="addLabels"
          data-cy="dialog-customLabels-ok"
        >
          {{ $t('routes.pod.menuEditLabels.addLabels') }}
        </v-btn>
        <v-btn variant="text" @click="dialog = false" class="btnCancel" data-cy="dialog-customLabels-false">
          {{ $t('common.cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useLabelStore } from '@/stores/LabelStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';

export default {
  mixins: [ DialogActionKeys, DialogModel ],
  name: 'SelectLabelsDialog',
  props: {
    isProcessingLabels: {
      type: Boolean,
      required: true,
    },
    showCustomLabels: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: [ 'addLabels' ],
  data: function () {
    return {
      selectedLabels: [],
      loading: false,
      activeTab: 'predefined',
      customLabelInput: '',
      errorMessage: '',
      includeSubfolders: false,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  watch: {
    dialog: function (val) {
      if (val) {
        this.selectedCategory = null;
        this.selectedLabels = [];
        this.errorMessage = '';
        this.activeTab = 'predefined';
        this.customLabelInput = '';
        this.includeSubfolders = false;
        this.loading = true;
        this.refreshCustomLabelCategories();
      }
    },
  },
  methods: {
    addLabels() {
      this.$emit('addLabels', { labels: this.selectedLabels, includeSubfolders: this.includeSubfolders });
      this.dialog = false;
    },
    removeLabel(idx) {
      this.selectedLabels.splice(idx, 1);
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27) {
        this.dialog = false;
      }
    },
    refreshCustomLabelCategories() {
      // If there is no access point ID, then we won't be able to fetch custom categories.
      // That is a problem when someone change the access point of a user and the user is still in the same page.
      if (!this.accessPointId) {
        return;
      }

      const payload = {
        accessPointId: this.accessPointId,
        includeLabels: true,
      };

      const labelStore = useLabelStore();
      labelStore.refreshCategories(payload)
        .catch((error) => {
          console.error('Error fetching custom categories: ', error);
        })
        .finally(() => {
          setTimeout(() => {
            this.loading = false;
          }, 500);
        });
    },
    addCustomLabel() {
      this.errorMessage = '';

      const processedLabel = this.customLabelInput.trim();
      if (processedLabel !== '') {
        const labelExists = this.selectedLabels.some((v) => v.name === processedLabel);

        if (labelExists) {
          this.errorMessage = this.$t('routes.pod.menuEditLabels.labelAlreadyExists');
        } else {
          this.selectedLabels.push({
            id: `custom-label-${Date.now()}`,
            name: processedLabel,
            isCustom: true,
          });
        }
      }

      this.customLabelInput = '';
    },
  },
  computed: {
    accessPointId() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    hasSelectedLabels() {
      return this.selectedLabels.length > 0;
    },
    hasLabels() {
      return this.categories.length > 0;
    },
    categories() {
      const labelStore = useLabelStore();
      return labelStore.getCategories;
    },
    selectedPods() {
      const podStore = usePodStore();
      return podStore.selectedPodIds;
    },
    selectedNodes() {
      const nodeStore = useNodeStore();
      return nodeStore.selectedNodeIds;
    },
    selectLabelsDialogTitle() {
      if (this.selectedPods.length > 0 && this.selectedNodes.length > 0) {
        return this.$t('routes.pod.menuEditLabels.addLabelsForFilesAndFolders');
      } else if (this.selectedPods.length > 0) {
        return this.$t('routes.pod.menuEditLabels.addLabelsForFiles', this.selectedPods.length, { n: this.selectedPods.length });
      } else if (this.selectedNodes.length > 0) {
        return this.$t('routes.pod.menuEditLabels.addLabelsForFolders', this.selectedNodes.length, { n: this.selectedNodes.length });
      } else {
        return this.$t('routes.pod.menuEditLabels.addLabels');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.labels-tree .v-list-item-title) {
  word-break: break-all;
  white-space: wrap;
}
</style>
